import { BarChartWidget } from "../charts/BarChartWidget";
import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtCurrency, fmtPercent, utilizationColor } from "./formatters";

interface BudgetRow {
  budgetId: string;
  budgetName: string;
  budgetCode: string;
  projectName: string | null;
  totalAmount: number;
  currency: string;
  approvedExpenses: number;
  pendingExpenses: number;
  utilizationPercent: number;
  remainingAmount: number;
  status: string;
}

interface BudgetVsActualData {
  data: BudgetRow[];
  summary: {
    totalBudget: number;
    totalSpent: number;
    totalRemaining: number;
    avgUtilization: number;
    overBudgetCount: number;
  };
}

function StatusIndicator({ pct }: { pct: number }) {
  if (pct > 100) {
    return <span className="text-[hsl(0,72%,45%)]">🔴 Over budget</span>;
  }
  if (pct > 90) {
    return <span className="text-[hsl(38,92%,30%)]">⚠️ At risk</span>;
  }
  return <span className="text-[hsl(142,72%,32%)]">✅ On track</span>;
}

export function BudgetVsActualReport({ data }: { data: BudgetVsActualData }) {
  const chartData = data.data.slice(0, 12).map((r) => ({
    name:
      r.budgetName.length > 14 ? r.budgetName.slice(0, 12) + "…" : r.budgetName,
    Allocated: r.totalAmount,
    Spent: r.approvedExpenses,
  }));

  const cols: Array<TableColumn<BudgetRow>> = [
    {
      key: "budget",
      label: "Budget",
      render: (r) => (
        <div>
          <div className="font-medium text-foreground">{r.budgetName}</div>
          <div className="font-mono text-[11px] text-muted-foreground">
            {r.budgetCode}
          </div>
        </div>
      ),
    },
    { key: "projectName", label: "Project", render: (r) => r.projectName ?? "—" },
    {
      key: "totalAmount",
      label: "Allocated",
      align: "right",
      render: (r) => fmtCurrency(r.totalAmount, r.currency),
    },
    {
      key: "approvedExpenses",
      label: "Spent",
      align: "right",
      render: (r) => fmtCurrency(r.approvedExpenses, r.currency),
    },
    {
      key: "remainingAmount",
      label: "Remaining",
      align: "right",
      render: (r) => fmtCurrency(r.remainingAmount, r.currency),
    },
    {
      key: "utilizationPercent",
      label: "% Used",
      align: "right",
      width: 120,
      render: (r) => (
        <span style={{ color: utilizationColor(r.utilizationPercent), fontWeight: 500 }}>
          {fmtPercent(r.utilizationPercent)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <StatusIndicator pct={r.utilizationPercent} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Total Budget"
          value={fmtCurrency(data.summary.totalBudget)}
        />
        <StatCard
          label="Total Spent"
          value={fmtCurrency(data.summary.totalSpent)}
        />
        <StatCard
          label="Total Remaining"
          value={fmtCurrency(data.summary.totalRemaining)}
          tone="good"
        />
        <StatCard
          label="Avg Utilization"
          value={fmtPercent(data.summary.avgUtilization)}
          tone={
            data.summary.avgUtilization > 90
              ? "bad"
              : data.summary.avgUtilization > 75
              ? "warn"
              : "good"
          }
        />
      </div>

      <ReportSection title="Allocated vs Spent">
        <BarChartWidget
          data={chartData}
          xKey="name"
          bars={[
            { key: "Allocated", name: "Allocated", color: "hsl(210,100%,50%)" },
            { key: "Spent", name: "Spent", color: "hsl(38,92%,50%)" },
          ]}
          yFormatter={(v) => fmtCurrency(v)}
          height={320}
        />
      </ReportSection>

      <ReportSection title="Budgets">
        <ReportTable
          columns={cols}
          rows={data.data}
          rowKey={(r) => r.budgetId}
          empty="No budgets match the current filters."
        />
      </ReportSection>
    </div>
  );
}
